import React from "react";
import styled from "styled-components";
import { useForceHTMLInputCheckedAttribute } from "../hooks/useForceHTMLInputCheckedAttribute";
import { Icon } from "../Icon";
import { style } from "../style";
import { forkedRef } from "../utils/forkedRef";

const INPUT_SIZE = "16px";

const LabelContainer = styled.label<{ $disabled: boolean }>`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  column-gap: 8px;

  &:focus-within {
    ${(props) => {
      if (props.$disabled) {
        return;
      }

      return {
        outline: `${style.vars.borderWidth.base} dotted ${style.colors.grey.dark}`,
        outlineOffset: style.vars.borderWidth.base,
      };
    }}
  }

  ${(props) => {
    if (props.$disabled) {
      return {
        opacity: "0.3",
        cursor: "unset",
      };
    }

    return {
      cursor: "pointer",
    };
  }};
`;

const OutputInputWrapper = styled.div`
  width: ${INPUT_SIZE};
  min-width: ${INPUT_SIZE};
  height: calc(${INPUT_SIZE} * ${style.vars.lineHeight});
  display: flex;
  align-items: center;
`;

const InputWrapper = styled.div`
  all: unset;

  box-sizing: border-box;

  width: ${INPUT_SIZE};
  min-width: ${INPUT_SIZE};
  height: ${INPUT_SIZE};
`;

type MainTextWrapperProps = {
  $checked: boolean;
  $disabled: boolean;
};

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleWrapper = styled.span<MainTextWrapperProps>`
  all: unset;
  font-size: ${style.vars.fontSize.base};
  line-height: ${style.vars.lineHeight};

  color: ${(props) => {
    if (props.$checked && !props.$disabled) {
      return style.colors.blue.dark;
    }

    return style.colors.grey.darkest;
  }};
`;

const SubtitleWrapper = styled.span`
  all: unset;
  font-size: ${style.vars.fontSize.small};
  font-weight: ${style.vars.fontWeight.base};
  line-height: ${style.vars.lineHeight};
  color: ${style.colors.grey.dark};
`;

const IconWrapper = styled.div`
  all: unset;
  box-sizing: border-box;
  min-width: 32px;
  margin-top: calc(-${INPUT_SIZE} / 4);
`;

type Props = {
  id?: string;
  value: string | null;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  checked: boolean;
  icon?: string | null;
  disabled?: boolean;
  children: React.FC<ChildrenProps>;
};

type ChildrenProps = {
  id?: string;
  "aria-labelledby"?: string;
  value: string;
  checked: boolean;
  disabled: boolean;
  ref: React.Ref<HTMLInputElement>;
};

export const PlainInputWrapper = React.forwardRef<HTMLInputElement, Props>((props, forwardedRef) => {
  const { id, value, title, subtitle, icon = null, disabled = false, checked } = props;

  const inputRef = React.useRef<HTMLInputElement>(null);
  const textRef = React.useRef<HTMLSpanElement>(null);

  const labelledBy = `${id}-label`;

  useForceHTMLInputCheckedAttribute(inputRef, checked);

  const input = props.children({
    id,
    "aria-labelledby": labelledBy,
    value: (value === null ? undefined : value) as string,
    checked,
    disabled,
    ref: forkedRef(forwardedRef, inputRef),
  });

  return (
    <LabelContainer htmlFor={id} $disabled={disabled}>
      <OutputInputWrapper>
        <InputWrapper>{input}</InputWrapper>
      </OutputInputWrapper>

      {icon !== null && (
        <IconWrapper>
          <Icon aria-hidden icon={icon} xlarge />
        </IconWrapper>
      )}

      <TextWrapper>
        <TitleWrapper ref={textRef} id={labelledBy} $checked={checked} $disabled={disabled}>
          {title}
        </TitleWrapper>
        {subtitle && <SubtitleWrapper>{subtitle}</SubtitleWrapper>}
      </TextWrapper>
    </LabelContainer>
  );
});

PlainInputWrapper.displayName = "PlainInputWrapper";
